<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Integrasi Lynk.id</h4>
            </div>
            <div v-if="hasLynkAddon" class="action-btn">
              <a @click="openAddModal" class="btn px-15 btn-primary text-white" style="display: inline-flex; margin: 5px">
                <i class="fas fa-plus fs-16"></i>
                Tambah Integrasi
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Alert: Addon Not Active -->
      <div v-if="!hasLynkAddon" class="row mb-4">
        <div class="col-12">
          <div class="alert alert-warning d-flex align-items-center" role="alert">
            <i class="fas fa-exclamation-triangle me-3 mr-3" style="font-size: 24px;"></i>
            <div class="flex-grow-1">
              <h5 class="alert-heading mb-2"><strong>Add-On Integrasi Lynk.id Belum Aktif</strong></h5>
              <p class="mb-2">Anda belum memiliki add-on Integrasi Lynk.id. Silakan upgrade paket Anda untuk mengaktifkan fitur ini.</p>
              <router-link to="/billing" class="btn btn-primary btn-sm">
                <i class="fas fa-shopping-cart me-2"></i>
                Upgrade Paket
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Info Alert (hanya saat addon aktif) -->
      <div v-if="hasLynkAddon" class="alert alert-info">
        <b>Cara pakai:</b>
        1) Klik <b>Tambah Integrasi</b> untuk buat webhook URL baru.
        2) Copy URL lalu paste di dashboard Lynk.id → menu Webhook.
        3) Setelah save di Lynk.id, Lynk akan kasih <b>Merchant Key</b> — paste kembali ke starsender.
        4) Setiap payment masuk di Lynk.id otomatis kirim WA sesuai template Anda.
      </div>

      <!-- Table -->
      <div v-if="hasLynkAddon" class="row">
        <div class="col-lg-12">
          <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else-if="integrations.length === 0" class="text-center text-muted p-4">
              <p>Belum ada integrasi. Klik tombol <b>Tambah Integrasi</b> untuk mulai.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="bg-light">
                  <tr>
                    <th>Nama</th>
                    <th>Webhook URL</th>
                    <th>Device</th>
                    <th>Status</th>
                    <th class="text-center">Total Hits</th>
                    <th>Last Hit</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="it in integrations" :key="it.id">
                    <td class="font-weight-bold">{{ it.name }}</td>
                    <td>
                      <div class="d-flex align-items-center" style="max-width: 340px;">
                        <input type="text" readonly :value="webhookUrl(it.webhook_token)" class="form-control form-control-sm"
                          style="font-family: monospace; font-size: 11px;" @focus="$event.target.select()">
                        <button class="btn btn-sm btn-light ml-2" @click="copyUrl(it.webhook_token)" title="Copy">
                          <i class="fas fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td>{{ it.device ? it.device.name : '-' }}</td>
                    <td>
                      <span v-if="it.is_active === 1" class="badge badge-success">Aktif</span>
                      <span v-else class="badge badge-secondary">Nonaktif</span>
                    </td>
                    <td class="text-center">{{ it.total_hits || 0 }}</td>
                    <td>{{ formatTime(it.last_hit_at) }}</td>
                    <td class="text-right">
                      <button class="btn btn-sm btn-primary" @click="openEditModal(it)" title="Edit">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button class="btn btn-sm btn-danger ml-1" @click="deleteItem(it.id)" title="Hapus">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <modal name="lynk_form" :height="'auto'" :adaptive="true" :scrollable="true" :width="'80%'">
        <div style="margin: 20px">
          <LynkForm :edit-item="editItem" :devices="devices" @saved="onSaved" />
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import LynkForm from "./Form.vue";
import moment from "moment";

export default {
  components: { LynkForm },
  data() {
    return {
      integrations: [],
      devices: [],
      isLoading: true,
      editItem: null,
    };
  },
  computed: {
    hasLynkAddon() {
      return this.$store.state.auth.package?.lynk_integration == 1;
    },
  },
  mounted() {
    if (this.hasLynkAddon) {
      this.loadData();
      this.loadDevices();
    } else {
      this.isLoading = false;
    }
  },
  methods: {
    webhookUrl(token) {
      // Derive from axios base: axios.defaults.baseURL is set to VUE_APP_BASE_URL
      const base = (this.$axios && this.$axios.defaults && this.$axios.defaults.baseURL)
        || (typeof window !== 'undefined' && window.axios && window.axios.defaults && window.axios.defaults.baseURL)
        || process.env.VUE_APP_BASE_URL
        || "https://api.starsender.online/api/v1";
      return base.replace(/\/+$/, "") + "/webhook/lynk/" + token;
    },
    formatTime(ts) {
      if (!ts) return "-";
      return moment(parseInt(ts)).format("YYYY-MM-DD HH:mm:ss");
    },
    async copyUrl(token) {
      try {
        await navigator.clipboard.writeText(this.webhookUrl(token));
        this.$toast.success("Webhook URL di-copy ke clipboard");
      } catch (err) {
        this.$toast.error("Gagal copy");
      }
    },
    async loadData() {
      this.isLoading = true;
      try {
        const res = await this.$store.dispatch("lynk_list");
        this.integrations = (res.data && res.data.data && res.data.data.integrations) || [];
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal memuat data");
      } finally {
        this.isLoading = false;
      }
    },
    async loadDevices() {
      try {
        const res = await this.$store.dispatch("device_list_all", { status: "all" });
        this.devices = (res.data && res.data.data && res.data.data.devices) || [];
      } catch (err) {
        // silent
      }
    },
    openAddModal() {
      this.editItem = null;
      this.$modal.show("lynk_form");
    },
    openEditModal(item) {
      this.editItem = { ...item };
      this.$modal.show("lynk_form");
    },
    async deleteItem(id) {
      if (!confirm("Yakin hapus integrasi ini? Webhook URL akan nonaktif permanen.")) return;
      try {
        await this.$store.dispatch("lynk_delete", id);
        this.$toast.success("Integrasi dihapus");
        this.loadData();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal menghapus");
      }
    },
    onSaved() {
      this.$modal.hide("lynk_form");
      this.loadData();
    },
  },
};
</script>
